import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, CHECKLIST_ITEMS } from "../../config";
import { translations, extras } from "../../i18n";
import { Loader2, Plus, Trash2, Save, RotateCcw, HelpCircle, BookOpen, Map, ListChecks, ClipboardCheck } from "lucide-react";

const LANGS = [["es", "Español"], ["en", "English"], ["de", "Deutsch"], ["it", "Italiano"]];
const emptyHistory = () => ({ eyebrow: "", title: "", sub: "", sticker: "", chapters: [] });
const emptySection = () => ({ eyebrow: "", title: "", items: [] });

// Editor de contenido de la web (FAQs, Historia, Rutas, «Sin letra pequeña» y protocolo de entrega) por idioma.
export default function ContentTab({ headers, canEdit }) {
  const [lang, setLang] = useState("es");
  const [faq, setFaq] = useState({});       // { es: [{q,a}], ... }
  const [history, setHistory] = useState({}); // { es: {eyebrow,title,sub,sticker,chapters[]}, ... }
  const [routes, setRoutes] = useState({});   // { es: {eyebrow,title,items:[{tag,t,d,img}]}, ... }
  const [cond, setCond] = useState({});       // { es: {eyebrow,title,items:[{t,d}]}, ... }
  const [protocol, setProtocol] = useState([]); // [{key,label}] — común a todos los idiomas
  const [insuranceNotes, setInsuranceNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/content`, { headers })
      .then(({ data }) => {
        setFaq(data.faq || {}); setHistory(data.history || {});
        setRoutes(data.routes || {}); setCond(data.cond || {});
        setProtocol((data.delivery_protocol || []).map((x) => ({ key: x.key, label: x.label })));
        setInsuranceNotes(data.insurance_notes || "");
      })
      .catch(() => toast.error("No se pudo cargar el contenido"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const faqItems = faq[lang] || [];
  const h = history[lang] || emptyHistory();
  const rt = routes[lang] || emptySection();
  const cd = cond[lang] || emptySection();
  const setFaqItems = (items) => setFaq({ ...faq, [lang]: items });
  const setH = (patch) => setHistory({ ...history, [lang]: { ...emptyHistory(), ...h, ...patch } });
  const setRt = (patch) => setRoutes({ ...routes, [lang]: { ...emptySection(), ...rt, ...patch } });
  const setCd = (patch) => setCond({ ...cond, [lang]: { ...emptySection(), ...cd, ...patch } });

  const loadDefaults = () => {
    const tr = translations[lang] || translations.es;
    const defFaq = tr?.faq?.items || [];
    const defHist = extras[lang]?.history || {};
    setFaq({ ...faq, [lang]: defFaq.map((i) => ({ q: i.q, a: i.a })) });
    setHistory({
      ...history,
      [lang]: {
        eyebrow: defHist.eyebrow || "", title: defHist.title || "", sub: defHist.sub || "",
        sticker: defHist.sticker || "",
        chapters: (defHist.chapters || []).map((c) => ({ n: c.n, year: c.year, t: c.t, d: c.d })),
      },
    });
    setRoutes({ ...routes, [lang]: { eyebrow: tr.routes?.eyebrow || "", title: tr.routes?.title || "", items: (tr.routes?.items || []).map((i) => ({ tag: i.tag || "", t: i.t || "", d: i.d || "", img: "" })) } });
    setCond({ ...cond, [lang]: { eyebrow: tr.cond?.eyebrow || "", title: tr.cond?.title || "", items: (tr.cond?.items || []).map((i) => ({ t: i.t || "", d: i.d || "" })) } });
    if (!protocol.length) setProtocol(CHECKLIST_ITEMS.map(([key, label]) => ({ key, label })));
    toast.success("Textos actuales cargados en el editor");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/content`, { faq, history, routes, cond, delivery_protocol: protocol, insurance_notes: insuranceNotes }, { headers });
      setFaq(data.faq || {}); setHistory(data.history || {});
      setRoutes(data.routes || {}); setCond(data.cond || {});
      setProtocol((data.delivery_protocol || []).map((x) => ({ key: x.key, label: x.label })));
      setInsuranceNotes(data.insurance_notes || "");
      toast.success("Contenido guardado y publicado");
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  // --- FAQ helpers ---
  const addFaq = () => setFaqItems([...faqItems, { q: "", a: "" }]);
  const updFaq = (i, k, v) => setFaqItems(faqItems.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const delFaq = (i) => setFaqItems(faqItems.filter((_, idx) => idx !== i));

  // --- History chapter helpers ---
  const addChapter = () => setH({ chapters: [...(h.chapters || []), { n: String((h.chapters?.length || 0) + 1).padStart(2, "0"), year: "", t: "", d: "" }] });
  const updChapter = (i, k, v) => setH({ chapters: h.chapters.map((c, idx) => idx === i ? { ...c, [k]: v } : c) });
  const delChapter = (i) => setH({ chapters: h.chapters.filter((_, idx) => idx !== i) });

  // --- Rutas helpers ---
  const addRoute = () => setRt({ items: [...(rt.items || []), { tag: "", t: "", d: "", img: "" }] });
  const updRoute = (i, k, v) => setRt({ items: rt.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const delRoute = (i) => setRt({ items: rt.items.filter((_, idx) => idx !== i) });

  // --- Sin letra pequeña helpers ---
  const addCond = () => setCd({ items: [...(cd.items || []), { t: "", d: "" }] });
  const updCond = (i, k, v) => setCd({ items: cd.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const delCond = (i) => setCd({ items: cd.items.filter((_, idx) => idx !== i) });

  // --- Protocolo de entrega helpers (común, no por idioma) ---
  const addProto = () => setProtocol([...protocol, { key: "", label: "" }]);
  const updProto = (i, v) => setProtocol(protocol.map((it, idx) => idx === i ? { ...it, label: v } : it));
  const delProto = (i) => setProtocol(protocol.filter((_, idx) => idx !== i));

  if (loading) return <p className="text-sm font-bold text-topacio-faded">Cargando contenido…</p>;

  return (
    <div data-testid="content-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-xl font-bold text-topacio-ink">Contenido de la web</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="content-lang-select" value={lang} onChange={(e) => setLang(e.target.value)} className="input-brutal !py-2 !w-auto !text-xs">
            {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {canEdit && (
            <>
              <button data-testid="content-load-defaults" onClick={loadDefaults} className="btn-secondary !px-4 !py-2 !text-xs flex items-center gap-1.5">
                <RotateCcw className="w-4 h-4" /> Cargar textos actuales
              </button>
              <button data-testid="content-save" onClick={save} disabled={saving} className="btn-primary !px-5 !py-2 !text-xs flex items-center gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar y publicar
              </button>
            </>
          )}
        </div>
      </div>
      <p className="text-[12px] text-topacio-faded font-semibold mb-6">
        Edita las preguntas frecuentes, «Nuestra historia», las rutas recomendadas y «Sin letra pequeña» por idioma, además del protocolo de entrega. Si dejas un idioma vacío, la web mostrará los textos por defecto. Pulsa «Cargar textos actuales» para partir de los que hay ahora.
      </p>

      {/* FAQ */}
      <div className="card-brutal p-6 mb-8">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-4"><HelpCircle className="w-4 h-4 text-topacio-red" /> Preguntas frecuentes ({faqItems.length})</h3>
        <div className="space-y-4">
          {faqItems.map((it, i) => (
            <div key={i} data-testid={`content-faq-${i}`} className="rounded-xl border-2 border-topacio-ink/15 bg-topacio-ink/[0.03] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-faded">Pregunta {i + 1}</span>
                {canEdit && <button data-testid={`content-faq-del-${i}`} onClick={() => delFaq(i)} className="text-topacio-red hover:scale-110 transition-transform"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <input data-testid={`content-faq-q-${i}`} value={it.q} disabled={!canEdit} onChange={(e) => updFaq(i, "q", e.target.value)} placeholder="Pregunta" className="input-brutal !py-2 !text-sm mb-2" />
              <textarea data-testid={`content-faq-a-${i}`} value={it.a} disabled={!canEdit} onChange={(e) => updFaq(i, "a", e.target.value)} rows={2} placeholder="Respuesta" className="input-brutal !py-2 !text-sm" />
            </div>
          ))}
          {faqItems.length === 0 && <p className="text-[12px] text-topacio-faded font-semibold">Sin preguntas en este idioma. Añade una o carga los textos actuales.</p>}
        </div>
        {canEdit && (
          <button data-testid="content-faq-add" onClick={addFaq} className="btn-secondary !px-4 !py-2 !text-xs flex items-center gap-1.5 mt-4"><Plus className="w-4 h-4" /> Añadir pregunta</button>
        )}
      </div>

      {/* Historia */}
      <div className="card-brutal p-6">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-4"><BookOpen className="w-4 h-4 text-topacio-red" /> Nuestra historia</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Antetítulo (eyebrow)</label>
            <input data-testid="content-hist-eyebrow" value={h.eyebrow} disabled={!canEdit} onChange={(e) => setH({ eyebrow: e.target.value })} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Pegatina (sticker)</label>
            <input data-testid="content-hist-sticker" value={h.sticker} disabled={!canEdit} onChange={(e) => setH({ sticker: e.target.value })} className="input-brutal mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Título</label>
            <input data-testid="content-hist-title" value={h.title} disabled={!canEdit} onChange={(e) => setH({ title: e.target.value })} className="input-brutal mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Subtítulo / intro</label>
            <textarea data-testid="content-hist-sub" value={h.sub} disabled={!canEdit} onChange={(e) => setH({ sub: e.target.value })} rows={2} className="input-brutal mt-1" />
          </div>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Capítulos ({h.chapters?.length || 0})</span>
            {canEdit && <button data-testid="content-hist-add-chapter" onClick={addChapter} className="btn-secondary !px-3 !py-1.5 !text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Añadir capítulo</button>}
          </div>
          <div className="space-y-3">
            {(h.chapters || []).map((c, i) => (
              <div key={i} data-testid={`content-hist-chapter-${i}`} className="rounded-xl border-2 border-topacio-ink/15 bg-topacio-ink/[0.03] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <input data-testid={`content-hist-ch-n-${i}`} value={c.n} disabled={!canEdit} onChange={(e) => updChapter(i, "n", e.target.value)} placeholder="Nº" className="input-brutal !py-2 !text-sm !w-16" />
                  <input data-testid={`content-hist-ch-year-${i}`} value={c.year} disabled={!canEdit} onChange={(e) => updChapter(i, "year", e.target.value)} placeholder="Año" className="input-brutal !py-2 !text-sm !w-24" />
                  <input data-testid={`content-hist-ch-t-${i}`} value={c.t} disabled={!canEdit} onChange={(e) => updChapter(i, "t", e.target.value)} placeholder="Título del capítulo" className="input-brutal !py-2 !text-sm flex-1" />
                  {canEdit && <button data-testid={`content-hist-ch-del-${i}`} onClick={() => delChapter(i)} className="text-topacio-red hover:scale-110 transition-transform shrink-0"><Trash2 className="w-4 h-4" /></button>}
                </div>
                <textarea data-testid={`content-hist-ch-d-${i}`} value={c.d} disabled={!canEdit} onChange={(e) => updChapter(i, "d", e.target.value)} rows={2} placeholder="Descripción" className="input-brutal !py-2 !text-sm" />
              </div>
            ))}
            {(!h.chapters || h.chapters.length === 0) && <p className="text-[12px] text-topacio-faded font-semibold">Sin capítulos en este idioma.</p>}
          </div>
        </div>
      </div>

      {/* Rutas recomendadas */}
      <div className="card-brutal p-6 mt-8">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-4"><Map className="w-4 h-4 text-topacio-red" /> Rutas recomendadas</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Antetítulo</label>
            <input data-testid="content-routes-eyebrow" value={rt.eyebrow} disabled={!canEdit} onChange={(e) => setRt({ eyebrow: e.target.value })} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Título</label>
            <input data-testid="content-routes-title" value={rt.title} disabled={!canEdit} onChange={(e) => setRt({ title: e.target.value })} className="input-brutal mt-1" />
          </div>
        </div>
        <div className="space-y-3">
          {(rt.items || []).map((it, i) => (
            <div key={i} data-testid={`content-route-${i}`} className="rounded-xl border-2 border-topacio-ink/15 bg-topacio-ink/[0.03] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-faded">Ruta {i + 1}</span>
                {canEdit && <button data-testid={`content-route-del-${i}`} onClick={() => delRoute(i)} className="text-topacio-red hover:scale-110 transition-transform"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="flex gap-2 mb-2">
                <input data-testid={`content-route-tag-${i}`} value={it.tag} disabled={!canEdit} onChange={(e) => updRoute(i, "tag", e.target.value)} placeholder="Etiqueta (ej. 2-4 h · Volcán)" className="input-brutal !py-2 !text-sm !w-1/3" />
                <input data-testid={`content-route-t-${i}`} value={it.t} disabled={!canEdit} onChange={(e) => updRoute(i, "t", e.target.value)} placeholder="Nombre (ej. El Teide)" className="input-brutal !py-2 !text-sm flex-1" />
              </div>
              <textarea data-testid={`content-route-d-${i}`} value={it.d} disabled={!canEdit} onChange={(e) => updRoute(i, "d", e.target.value)} rows={2} placeholder="Descripción" className="input-brutal !py-2 !text-sm mb-2" />
              <input data-testid={`content-route-img-${i}`} value={it.img || ""} disabled={!canEdit} onChange={(e) => updRoute(i, "img", e.target.value)} placeholder="Enlace de la foto (https://…) — opcional" className="input-brutal !py-2 !text-sm" />
              {it.img ? <img src={it.img} alt="" className="mt-2 h-20 w-full object-cover rounded-lg border-2 border-topacio-ink/15" onError={(e) => { e.target.style.display = "none"; }} /> : null}
            </div>
          ))}
          {(!rt.items || rt.items.length === 0) && <p className="text-[12px] text-topacio-faded font-semibold">Sin rutas en este idioma. Añade una o carga los textos actuales.</p>}
        </div>
        {canEdit && <button data-testid="content-route-add" onClick={addRoute} className="btn-secondary !px-4 !py-2 !text-xs flex items-center gap-1.5 mt-4"><Plus className="w-4 h-4" /> Añadir ruta</button>}
      </div>

      {/* Sin letra pequeña (condiciones) */}
      <div className="card-brutal p-6 mt-8">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-4"><ListChecks className="w-4 h-4 text-topacio-red" /> Sin letra pequeña</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Antetítulo</label>
            <input data-testid="content-cond-eyebrow" value={cd.eyebrow} disabled={!canEdit} onChange={(e) => setCd({ eyebrow: e.target.value })} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Título</label>
            <input data-testid="content-cond-title" value={cd.title} disabled={!canEdit} onChange={(e) => setCd({ title: e.target.value })} className="input-brutal mt-1" />
          </div>
        </div>
        <div className="space-y-3">
          {(cd.items || []).map((it, i) => (
            <div key={i} data-testid={`content-cond-${i}`} className="rounded-xl border-2 border-topacio-ink/15 bg-topacio-ink/[0.03] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-topacio-faded">Tarjeta {i + 1}</span>
                {canEdit && <button data-testid={`content-cond-del-${i}`} onClick={() => delCond(i)} className="text-topacio-red hover:scale-110 transition-transform"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <input data-testid={`content-cond-t-${i}`} value={it.t} disabled={!canEdit} onChange={(e) => updCond(i, "t", e.target.value)} placeholder="Título (ej. Carnet de conducir)" className="input-brutal !py-2 !text-sm mb-2" />
              <textarea data-testid={`content-cond-d-${i}`} value={it.d} disabled={!canEdit} onChange={(e) => updCond(i, "d", e.target.value)} rows={2} placeholder="Descripción" className="input-brutal !py-2 !text-sm" />
            </div>
          ))}
          {(!cd.items || cd.items.length === 0) && <p className="text-[12px] text-topacio-faded font-semibold">Sin tarjetas en este idioma. Añade una o carga los textos actuales.</p>}
        </div>
        {canEdit && <button data-testid="content-cond-add" onClick={addCond} className="btn-secondary !px-4 !py-2 !text-xs flex items-center gap-1.5 mt-4"><Plus className="w-4 h-4" /> Añadir tarjeta</button>}
      </div>

      {/* Protocolo de entrega (checklist de revisión — común a todos los idiomas) */}
      <div className="card-brutal p-6 mt-8" data-testid="content-protocol">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-1"><ClipboardCheck className="w-4 h-4 text-topacio-red" /> Protocolo de entrega — Revisión del vehículo</h3>
        <p className="text-[12px] text-topacio-faded font-semibold mb-4">Puntos que marca el equipo en la entrega y la devolución (salen también en el contrato PDF). Es común para todos los idiomas.</p>
        <div className="space-y-2">
          {protocol.map((it, i) => (
            <div key={i} data-testid={`content-proto-${i}`} className="flex items-center gap-2">
              <input data-testid={`content-proto-label-${i}`} value={it.label} disabled={!canEdit} onChange={(e) => updProto(i, e.target.value)} placeholder="Punto a revisar (ej. Neumáticos en buen estado)" className="input-brutal !py-2 !text-sm flex-1" />
              {canEdit && <button data-testid={`content-proto-del-${i}`} onClick={() => delProto(i)} className="text-topacio-red hover:scale-110 transition-transform shrink-0"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {protocol.length === 0 && <p className="text-[12px] text-topacio-faded font-semibold">Sin puntos. Añade uno o pulsa «Cargar textos actuales».</p>}
        </div>
        {canEdit && <button data-testid="content-proto-add" onClick={addProto} className="btn-secondary !px-4 !py-2 !text-xs flex items-center gap-1.5 mt-4"><Plus className="w-4 h-4" /> Añadir punto</button>}
      </div>

      {/* Observaciones del seguro (salen en el contrato PDF) */}
      <div className="card-brutal p-6 mt-8" data-testid="content-insurance">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-1"><ClipboardCheck className="w-4 h-4 text-topacio-red" /> Observaciones del seguro (contrato)</h3>
        <p className="text-[12px] text-topacio-faded font-semibold mb-3">Texto en español que aparece en el contrato PDF. Si lo dejas vacío, se usa una cláusula estándar.</p>
        <textarea data-testid="content-insurance-notes" value={insuranceNotes} disabled={!canEdit} maxLength={400}
          onChange={(e) => setInsuranceNotes(e.target.value)} rows={3}
          placeholder="Ej. Seguro obligatorio de RC incluido. Daños propios y robo no cubiertos salvo Todo Riesgo. Franquicia según grupo…"
          className="input-brutal !text-sm" />
      </div>
    </div>
  );
}
